import React,{useRef,useEffect,useState,useCallback} from 'react';
import Bubble,{StreamingBubble} from './Bubble';
import Subheading from '../Text/Subheading';
import Text from '../Text/Text';
import Icon from '../Text/Icon';
import {SLASH_COMMANDS} from './CommandPalette';
import classNames from 'classnames';

export default function ChatContainer({
    chats,
    onDeleteBubble,
    isProcessing=false,
    currentToolCalls=[],
    thinkingState=null,
    streamingMessage=null,
    userName=null,
    searchQuery=''
}) {
    const scrollRef=useRef(null);
    const [isNearBottom,setIsNearBottom]=useState(true);
    const [hasNewMessages,setHasNewMessages]=useState(false);

    const checkIfNearBottom=useCallback(() => {
        const el=scrollRef.current;
        if(!el) return true;
        return el.scrollHeight-el.scrollTop-el.clientHeight<80;
    },[]);

    const handleScroll=useCallback(() => {
        const near=checkIfNearBottom();
        setIsNearBottom(near);
        if(near) setHasNewMessages(false);
    },[checkIfNearBottom]);

    const scrollToBottom=useCallback(() => {
        if(scrollRef.current) {
            scrollRef.current.scrollTo({top: scrollRef.current.scrollHeight,behavior: 'smooth'});
            setHasNewMessages(false);
        }
    },[]);

    // Smooth auto-scroll when new content arrives, only if near bottom
    useEffect(() => {
        if(isNearBottom) {
            scrollToBottom();
        } else {
            setHasNewMessages(true);
        }
    },[chats.length,currentToolCalls.length,thinkingState,isNearBottom,scrollToBottom]);

    return (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden" role="log" aria-label="Chat messages" aria-live="polite">
            {chats.length===0&&!isProcessing? (
                <div className="flex flex-col items-center justify-center w-full h-full gap-3 px-4">
                    <Subheading className="text-lg font-medium text-center text-dark/50">
                        {userName? `Hi ${userName}, ask me anything`:'Ask me anything'}
                    </Subheading>
                    <Text className="text-sm text-center text-dark/40 max-w-md">
                        Get help with your flowchart using commands:
                    </Text>
                    <div className="grid grid-cols-2 gap-2 mt-2 w-full max-w-sm">
                        {Object.values(SLASH_COMMANDS).map(cmd => (
                            <div key={cmd.name} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-gray-50">
                                <div className={classNames('flex items-center justify-center w-6 h-6 rounded',cmd.bgColor)}>
                                    <Icon icon={cmd.icon} className={classNames('text-xs',cmd.color)} />
                                </div>
                                <div className="min-w-0">
                                    <Text className="text-xs font-semibold text-dark/60 truncate">{cmd.name}</Text>
                                    <Text className="text-[10px] text-dark/40 truncate">{cmd.description}</Text>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ):(
                <div className="relative flex-1 min-h-0">
                    <div
                        ref={scrollRef}
                        onScroll={handleScroll}
                        className="overflow-y-auto h-full py-4 px-2 scroll-smooth"
                    >
                        {(searchQuery
                            ? chats.filter(c=>c.content?.toLowerCase().includes(searchQuery.toLowerCase()))
                            : chats
                        ).map((chat,index) => (
                            <Bubble
                                key={chat.id||index}
                                chat={chat}
                                index={index}
                                label={!chat?.user||chats[Math.min(index+1,chats?.length-1)]?.user!==chat?.user}
                                deleteChatBubble={onDeleteBubble}
                                highlight={searchQuery}
                            />
                        ))}

                        {/* Show streaming bubble while processing */}
                        {isProcessing&&(
                            <StreamingBubble
                                toolCalls={currentToolCalls}
                                thinkingState={thinkingState}
                                streamingMessage={streamingMessage}
                            />
                        )}
                    </div>

                    {/* New messages indicator */}
                    {hasNewMessages&&(
                        <button
                            onClick={scrollToBottom}
                            className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 text-xs font-medium text-white bg-primary-500 rounded-full shadow-lg hover:bg-primary-600 transition-all animate-bounce cursor-pointer"
                        >
                            <Icon icon='arrow-down' className='inline mr-1 text-xs' />
                            New messages
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
